import { getApiBase, safeJson, unwrapArray } from "./http";
import type { TicketAssignment, Ticket } from "../types";

export const fetchTicketsApi = async (
    eventId: number
): Promise<Ticket[]> => {
    const base = getApiBase();

    const res = await fetch(
        `${base}/api/tickets?eventId=${eventId}`
    );

    if (!res.ok) {
        throw new Error("Kunne ikke hente tickets");
    }

    const data = await safeJson<any>(res);

    return (data as Ticket[]) || [];
};

export const toApiDateTime = (value?: string | null) => {
    if (!value) return null;
    return value.length === 16 ? `${value}:00` : value;
};

export const saveTicketApi = async (ticket: Ticket): Promise<void> => {
    const base = getApiBase();
    const method = ticket.ticketID ? "PUT" : "POST";
    const url = ticket.ticketID
        ? `${base}/api/tickets/${ticket.ticketID}`
        : `${base}/api/tickets`;

    const payload = {
        ...ticket,
        ticketTimeStart: toApiDateTime(ticket.ticketTimeStart),
        ticketTimeEnd: toApiDateTime(ticket.ticketTimeEnd),
    };

    const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
    });

    if (!res.ok) {
        const text = await res.text();
        throw new Error(text || "Kunne ikke gemme ticket");
    }
};

export const deleteTicketApi = async (ticketID: number): Promise<void> => {
    const base = getApiBase();
    await fetch(`${base}/api/tickets/${ticketID}`, { method: "DELETE" });
};

export const fetchAssignmentsApi = async (
    eventId: number
): Promise<TicketAssignment[]> => {
    const base = getApiBase();

    const res = await fetch(
        `${base}/api/tickets/assignments?eventId=${eventId}`
    );

    if (!res.ok) return [];

    const data = await safeJson<any>(res);
    const arr = unwrapArray(data);

    return arr.map((a: any) => ({
        ticketAssignmentID:
            a.ticketAssignmentID ?? a.TicketAssignmentID,

        ticketID:
            a.ticketID ?? a.TicketID,

        eventID:
            a.eventID ?? a.EventID,

        personID:
            a.personID ?? a.PersonID ?? null,

        shiftsID:
            a.shiftsID ?? a.ShiftsID ?? null,

        functionID:
            a.functionID ?? a.FunctionID ?? null,

        usedAt:
            a.usedAt ?? a.UsedAt ?? null,

        ticketDescription:
            a.ticketDescription ?? a.TicketDescription ?? "",

        ticketTimeStart:
            a.ticketTimeStart ?? a.TicketTimeStart ?? "",

        ticketTimeEnd:
            a.ticketTimeEnd ?? a.TicketTimeEnd ?? "",

        active:
            a.active ?? a.Active ?? false,
    })) as TicketAssignment[];
};

export const assignTargetApi = async (
    ticketID: number,
    targetType: "person" | "shift" | "function",
    targetID: number
): Promise<{ ok: boolean; message?: string }> => {
    const base = getApiBase();
    const res = await fetch(`${base}/api/tickets/assign-target`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ticketID, targetType, targetID }),
    });

    if (!res.ok) {
        const err = await safeJson<any>(res);
        return { ok: false, message: err?.Message || "Kunne ikke tildele ticket" };
    }
    return { ok: true };
};

export const deleteAssignmentApi = async (
    ticketAssignmentID: number
): Promise<boolean> => {
    const base = getApiBase();
    const res = await fetch(`${base}/api/tickets/assignments/${ticketAssignmentID}`, {
        method: "DELETE",
    });
    return res.ok;
};